import React, { useState, useRef, useEffect } from 'react';
import Turntable from './Turntable';
import './Djpanel.css';
import music from './assetts/pitbull_comp.mp3';

const baseTempo = 126;

const performancePads = [
    { label: 'Cue 1', action: 'cue', fraction: 0 },
    { label: 'Cue 2', action: 'cue', fraction: 0.12 },
    { label: 'Vocal', action: 'cue', fraction: 0.22 },
    { label: 'Build', action: 'cue', fraction: 0.34 },
    { label: 'Drop', action: 'cue', fraction: 0.48, autoPlay: true },
    { label: 'Break', action: 'cue', fraction: 0.63 },
    { label: 'Loop 8', action: 'loop' },
    { label: 'Stutter', action: 'stutter' },
    { label: 'Back 8', action: 'seek', seconds: -8 },
    { label: 'Ahead 8', action: 'seek', seconds: 8 },
    { label: 'Brake', action: 'brake' },
    { label: 'Kill', action: 'kill' },
];

const channelDefaults = [
    { id: 'deckA', label: 'Deck A', value: 86 },
    { id: 'deckB', label: 'Deck B', value: 78 },
    { id: 'vox', label: 'Vox', value: 64 },
    { id: 'fx', label: 'FX', value: 72 },
];

const effects = ['Filter', 'Delay', 'Reverb', 'Drive', 'Gate', 'Wide'];
const eqDefaults = { low: 58, mid: 52, high: 64, trim: 70 };

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const safeDuration = (audio) => (Number.isFinite(audio?.duration) && audio.duration > 0 ? audio.duration : 0);

function formatTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${remainder}`;
}

function makeDistortionCurve(amount = 0) {
    if (!amount) return null;
    const samples = 44100;
    const curve = new Float32Array(samples);
    const deg = Math.PI / 180;

    for (let i = 0; i < samples; i += 1) {
        const x = (i * 2) / samples - 1;
        curve[i] = ((3 + amount) * x * 20 * deg) / (Math.PI + amount * Math.abs(x));
    }

    return curve;
}

function createImpulseResponse(context) {
    const duration = 1.35;
    const decay = 2.4;
    const length = context.sampleRate * duration;
    const impulse = context.createBuffer(2, length, context.sampleRate);

    for (let channel = 0; channel < 2; channel += 1) {
        const data = impulse.getChannelData(channel);
        for (let i = 0; i < length; i += 1) {
            data[i] = (Math.random() * 2 - 1) * ((1 - i / length) ** decay);
        }
    }

    return impulse;
}

function DjPanel() {
    const [isPlaying, setIsPlaying] = useState(false);
    const [activePads, setActivePads] = useState(['Cue 1']);
    const [activeEffects, setActiveEffects] = useState(['Filter']);
    const [tempo, setTempo] = useState(baseTempo);
    const [masterLevel, setMasterLevel] = useState(84);
    const [crossfader, setCrossfader] = useState(50);
    const [eq, setEq] = useState(eqDefaults);
    const [channelLevels, setChannelLevels] = useState(() => Object.fromEntries(channelDefaults.map((channel) => [channel.id, channel.value])));
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [loopEnabled, setLoopEnabled] = useState(false);
    const [loopWindow, setLoopWindow] = useState({ start: 0, end: 8 });
    const [isMuted, setIsMuted] = useState(false);
    const [hypeMode, setHypeMode] = useState(false);
    const [lastAction, setLastAction] = useState('Ready');
    const audioRef = useRef();
    const canvasRef = useRef();
    const audioContextRef = useRef(null);
    const graphRef = useRef(null);
    const analyserRef = useRef();

    const applyAudioSettings = (graph = graphRef.current) => {
        const audio = audioRef.current;
        if (!audio) return;

        audio.playbackRate = clamp(tempo / baseTempo, 0.65, 1.45);
        audio.volume = graph ? 1 : (isMuted ? 0 : masterLevel / 100);

        if (!graph) return;

        const context = audioContextRef.current;
        const now = context?.currentTime ?? 0;
        const hasEffect = (effect) => activeEffects.includes(effect);
        const deckBlend = ((channelLevels.deckA / 100) * (1 - crossfader / 100)) + ((channelLevels.deckB / 100) * (crossfader / 100));
        const supportBlend = (channelLevels.vox / 100) * 0.12 + (channelLevels.fx / 100) * (activeEffects.length ? 0.16 : 0.06);
        const channelMix = clamp(deckBlend + supportBlend, 0.08, 1.18);
        const hypeGain = hypeMode ? 1.08 : 1;
        const masterGain = (masterLevel / 100) * channelMix * hypeGain * (isMuted ? 0 : 1) * (hasEffect('Gate') ? 0.72 : 1);

        graph.masterGain.gain.setTargetAtTime(masterGain, now, 0.035);
        graph.lowShelf.gain.setTargetAtTime((eq.low - 50) / 4, now, 0.04);
        graph.midPeak.gain.setTargetAtTime((eq.mid - 50) / 5, now, 0.04);
        graph.highShelf.gain.setTargetAtTime((eq.high - 50) / 4, now, 0.04);
        graph.trimGain.gain.setTargetAtTime(eq.trim / 70, now, 0.04);
        graph.delayWet.gain.setTargetAtTime(hasEffect('Delay') ? (hypeMode ? 0.36 : 0.28) : 0, now, 0.04);
        graph.feedback.gain.setTargetAtTime(hasEffect('Delay') ? (hypeMode ? 0.38 : 0.32) : 0, now, 0.04);
        graph.reverbGain.gain.setTargetAtTime(hasEffect('Reverb') ? (hypeMode ? 0.28 : 0.22) : 0, now, 0.04);
        graph.colorFilter.frequency.setTargetAtTime(hasEffect('Filter') ? (hypeMode ? 2200 : 1650) : 20000, now, 0.04);
        graph.colorFilter.Q.setTargetAtTime(hasEffect('Filter') ? 7 : 0.7, now, 0.04);
        graph.drive.curve = hasEffect('Drive') ? makeDistortionCurve(420) : null;

        if (graph.panner) {
            const pan = hasEffect('Wide') ? ((crossfader - 50) / 50) : ((crossfader - 50) / 85);
            graph.panner.pan.setTargetAtTime(clamp(pan, -1, 1), now, 0.04);
        }
    };

    const ensureAudioGraph = async () => {
        if (graphRef.current) {
            if (audioContextRef.current?.state === 'suspended') await audioContextRef.current.resume();
            applyAudioSettings();
            return;
        }

        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        const context = new AudioContextClass();
        audioContextRef.current = context;

        const source = context.createMediaElementSource(audioRef.current);
        const drive = context.createWaveShaper();
        const lowShelf = context.createBiquadFilter();
        const midPeak = context.createBiquadFilter();
        const highShelf = context.createBiquadFilter();
        const colorFilter = context.createBiquadFilter();
        const trimGain = context.createGain();
        const masterGain = context.createGain();
        const analyser = context.createAnalyser();
        const delay = context.createDelay(1);
        const feedback = context.createGain();
        const delayWet = context.createGain();
        const reverb = context.createConvolver();
        const reverbGain = context.createGain();
        const panner = context.createStereoPanner ? context.createStereoPanner() : null;

        lowShelf.type = 'lowshelf';
        lowShelf.frequency.value = 320;
        midPeak.type = 'peaking';
        midPeak.frequency.value = 1100;
        midPeak.Q.value = 0.9;
        highShelf.type = 'highshelf';
        highShelf.frequency.value = 3600;
        colorFilter.type = 'lowpass';
        colorFilter.frequency.value = 20000;
        delay.delayTime.value = 0.28;
        delayWet.gain.value = 0;
        feedback.gain.value = 0;
        reverb.buffer = createImpulseResponse(context);
        reverbGain.gain.value = 0;
        analyser.fftSize = 1024;

        source.connect(drive);
        drive.connect(lowShelf);
        lowShelf.connect(midPeak);
        midPeak.connect(highShelf);
        highShelf.connect(colorFilter);
        colorFilter.connect(trimGain);

        if (panner) {
            trimGain.connect(panner);
            panner.connect(masterGain);
        } else {
            trimGain.connect(masterGain);
        }

        trimGain.connect(delay);
        delay.connect(feedback);
        feedback.connect(delay);
        delay.connect(delayWet);
        delayWet.connect(masterGain);
        trimGain.connect(reverb);
        reverb.connect(reverbGain);
        reverbGain.connect(masterGain);
        masterGain.connect(analyser);
        analyser.connect(context.destination);

        graphRef.current = {
            source,
            drive,
            lowShelf,
            midPeak,
            highShelf,
            colorFilter,
            trimGain,
            masterGain,
            analyser,
            delay,
            feedback,
            delayWet,
            reverb,
            reverbGain,
            panner,
        };
        analyserRef.current = analyser;
        applyAudioSettings(graphRef.current);

        if (context.state === 'suspended') await context.resume();
    };

    const playAudio = async () => {
        await ensureAudioGraph();
        await audioRef.current.play();
        setIsPlaying(true);
    };

    const handlePlayPause = async () => {
        if (!audioRef.current) return;

        if (audioRef.current.paused) {
            await playAudio();
            setLastAction('Playing');
        } else {
            audioRef.current.pause();
            setIsPlaying(false);
            setLastAction('Paused');
        }
    };

    const seekTo = (seconds) => {
        const audio = audioRef.current;
        if (!audio) return;
        const maxDuration = safeDuration(audio);
        audio.currentTime = clamp(seconds, 0, maxDuration || seconds);
        setCurrentTime(audio.currentTime);
    };

    const jumpToFraction = async (fraction, autoPlay) => {
        const audio = audioRef.current;
        if (!audio) return;
        const maxDuration = safeDuration(audio);
        seekTo(maxDuration * fraction);
        if (autoPlay || isPlaying) await playAudio();
    };

    const togglePad = async (pad) => {
        setActivePads((currentPads) => currentPads.includes(pad.label)
            ? currentPads.filter((activePad) => activePad !== pad.label)
            : [...currentPads.slice(-3), pad.label]);
        setLastAction(pad.label);

        if (pad.action === 'cue') {
            await jumpToFraction(pad.fraction, pad.autoPlay);
            return;
        }

        if (pad.action === 'loop') {
            const audio = audioRef.current;
            const start = audio?.currentTime ?? 0;
            const end = Math.min((safeDuration(audio) || start + 8), start + 8);
            setLoopWindow({ start, end });
            setLoopEnabled((enabled) => !enabled);
            return;
        }

        if (pad.action === 'stutter') {
            seekTo((audioRef.current?.currentTime ?? 0) - 0.42);
            if (isPlaying) await playAudio();
            return;
        }

        if (pad.action === 'seek') {
            seekTo((audioRef.current?.currentTime ?? 0) + pad.seconds);
            return;
        }

        if (pad.action === 'brake') {
            setTempo(92);
            window.setTimeout(() => setTempo(baseTempo), 650);
            return;
        }

        if (pad.action === 'kill') {
            setIsMuted((muted) => !muted);
        }
    };

    const toggleEffect = (effect) => {
        setActiveEffects((currentEffects) => currentEffects.includes(effect)
            ? currentEffects.filter((activeEffect) => activeEffect !== effect)
            : [...currentEffects, effect]);
        setLastAction(`${effect} ${activeEffects.includes(effect) ? 'off' : 'on'}`);
    };

    const updateChannel = (id, value) => {
        setChannelLevels((levels) => ({ ...levels, [id]: value }));
    };

    const updateEq = (id, value) => {
        setEq((currentEq) => ({ ...currentEq, [id]: value }));
    };

    const toggleHypeMode = async () => {
        const nextHypeMode = !hypeMode;
        setHypeMode(nextHypeMode);

        if (nextHypeMode) {
            setTempo(132);
            setMasterLevel(92);
            setCrossfader(56);
            setActiveEffects((currentEffects) => Array.from(new Set([...currentEffects, 'Filter', 'Delay', 'Reverb', 'Wide'])));
            setLastAction('Hype Mode');
            if (!isPlaying) await playAudio();
            return;
        }

        setTempo(baseTempo);
        setCrossfader(50);
        setLastAction('Hype Off');
    };

    useEffect(() => {
        applyAudioSettings();
    }, [activeEffects, tempo, masterLevel, crossfader, eq, isMuted, hypeMode, channelLevels]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return undefined;

        const handleTimeUpdate = () => {
            if (loopEnabled && audio.currentTime >= loopWindow.end) {
                audio.currentTime = loopWindow.start;
            }
            setCurrentTime(audio.currentTime);
            setDuration(safeDuration(audio));
        };
        const handleLoadedMetadata = () => setDuration(safeDuration(audio));
        const handleEnded = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        audio.addEventListener('ended', handleEnded);
        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [loopEnabled, loopWindow]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ctx = canvas.getContext('2d');
        let animationFrame;

        const drawIdle = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = 'rgba(3, 9, 10, 0.82)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < 10; i += 1) {
                const y = (canvas.height / 10) * i;
                ctx.strokeStyle = `rgba(106, 213, 198, ${i % 2 === 0 ? 0.12 : 0.06})`;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(canvas.width, y);
                ctx.stroke();
            }

            ctx.strokeStyle = 'rgba(255, 211, 105, 0.55)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(0, canvas.height / 2);
            ctx.lineTo(canvas.width, canvas.height / 2);
            ctx.stroke();
        };

        const drawWaveform = () => {
            if (!isPlaying || !analyserRef.current) {
                drawIdle();
                return;
            }

            const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
            analyserRef.current.getByteTimeDomainData(dataArray);

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
            gradient.addColorStop(0, 'rgba(3, 9, 10, 0.9)');
            gradient.addColorStop(0.5, 'rgba(6, 30, 32, 0.84)');
            gradient.addColorStop(1, 'rgba(3, 9, 10, 0.96)');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            for (let i = 0; i < 12; i += 1) {
                const x = (canvas.width / 12) * i;
                ctx.strokeStyle = 'rgba(106, 213, 198, 0.09)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, canvas.height);
                ctx.stroke();
            }

            const sliceWidth = canvas.width / dataArray.length;
            let x = 0;

            ctx.beginPath();
            dataArray.forEach((point, index) => {
                const y = (point / 128.0) * (canvas.height / 2);
                if (index === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
                x += sliceWidth;
            });

            ctx.strokeStyle = '#6ad5c6';
            ctx.lineWidth = 3;
            ctx.shadowColor = 'rgba(106, 213, 198, 0.9)';
            ctx.shadowBlur = 16;
            ctx.stroke();
            ctx.shadowBlur = 0;

            animationFrame = requestAnimationFrame(drawWaveform);
        };

        drawWaveform();
        return () => cancelAnimationFrame(animationFrame);
    }, [isPlaying]);

    const activePadLabel = activePads.length ? activePads[activePads.length - 1] : 'Ready';
    const progress = duration ? (currentTime / duration) * 100 : 0;
    const energyLevel = clamp((isPlaying ? 48 : 16) + activeEffects.length * 7 + (masterLevel / 4), 6, 100);
    const meterValues = [
        energyLevel,
        clamp(energyLevel * (channelLevels.deckA / 100), 6, 100),
        clamp(energyLevel * (channelLevels.deckB / 100), 6, 100),
        clamp(energyLevel * (channelLevels.fx / 100), 6, 100),
    ];

    return (
        <div className={`djpanel-container ${isPlaying ? 'is-playing' : ''} ${loopEnabled ? 'is-looping' : ''} ${hypeMode ? 'is-hype' : ''}`}>
            <div className="dj-board-top">
                <div className="dj-board-title">
                    <span>DJ Fracking Board</span>
                    <h2>Live Mix Control</h2>
                </div>
                <div className="transport-cluster">
                    <button className="transport-button" type="button" onClick={handlePlayPause}>
                        <span className="transport-dot" aria-hidden="true"></span>
                        {isPlaying ? 'Pause Set' : 'Play Set'}
                    </button>
                    <button className={`mute-button ${isMuted ? 'active' : ''}`} type="button" onClick={() => setIsMuted((muted) => !muted)}>
                        {isMuted ? 'Muted' : 'Mute'}
                    </button>
                    <button className={`hype-button ${hypeMode ? 'active' : ''}`} type="button" onClick={toggleHypeMode}>
                        {hypeMode ? 'Hype On' : 'Hype'}
                    </button>
                </div>
            </div>

            <div className="dj-command-strip" aria-label="DJ board controls">
                <div className="status-screen" aria-live="polite">
                    <div>
                        <small>Status</small>
                        <strong>{hypeMode ? 'Hype Mode' : isPlaying ? 'Signal Live' : 'Standby'}</strong>
                    </div>
                    <div>
                        <small>Last Hit</small>
                        <strong>{activePadLabel}</strong>
                    </div>
                    <div>
                        <small>FX Chain</small>
                        <strong>{activeEffects.length ? activeEffects.join(' + ') : 'Dry'}</strong>
                    </div>
                    <div>
                        <small>Loop</small>
                        <strong>{loopEnabled ? `${formatTime(loopWindow.start)}-${formatTime(loopWindow.end)}` : 'Off'}</strong>
                    </div>
                </div>

                <div className="progress-panel">
                    <div className="time-row">
                        <span>{formatTime(currentTime)}</span>
                        <strong>{lastAction}</strong>
                        <span>{formatTime(duration)}</span>
                    </div>
                    <input
                        aria-label="Track position"
                        className="progress-slider"
                        type="range"
                        min="0"
                        max={duration || 100}
                        step="0.1"
                        value={duration ? currentTime : 0}
                        onChange={(event) => seekTo(Number(event.target.value))}
                        style={{ '--progress': `${progress}%` }}
                    />
                </div>
            </div>

            <div className="deck-row">
                <Turntable />
                <div className="central-mixer">
                    <div className="waveform-wrap">
                        <div className="scanner-line" aria-hidden="true"></div>
                        <canvas ref={canvasRef} className="waveform" width="520" height="150" />
                    </div>

                    <div className="meter-bridge" aria-label="VU meters">
                        {meterValues.map((level, index) => (
                            <div className="vu-meter" key={`meter-${index}`}>
                                <span className="vu-fill" style={{ '--level': `${level}%` }}></span>
                            </div>
                        ))}
                    </div>

                    <div className="channel-row">
                        {channelDefaults.map((channel) => (
                            <label className="channel-control" key={channel.id}>
                                <span>{channel.label}</span>
                                <input
                                    aria-label={`${channel.label} level`}
                                    className="dj-slider"
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={channelLevels[channel.id]}
                                    onChange={(event) => updateChannel(channel.id, Number(event.target.value))}
                                />
                            </label>
                        ))}
                    </div>

                    <label className="crossfader-control">
                        <span>Crossfader</span>
                        <input
                            aria-label="Crossfader"
                            className="dj-slider"
                            type="range"
                            min="0"
                            max="100"
                            value={crossfader}
                            onChange={(event) => setCrossfader(Number(event.target.value))}
                        />
                        <div className="crossfader-labels"><small>A</small><small>B</small></div>
                    </label>
                </div>
                <Turntable />
            </div>

            <div className="performance-pads" aria-label="Performance pads">
                {performancePads.map((pad) => (
                    <button
                        aria-pressed={activePads.includes(pad.label) || (pad.action === 'loop' && loopEnabled) || (pad.action === 'kill' && isMuted)}
                        className={`pad-button ${activePads.includes(pad.label) ? 'active' : ''} ${pad.action === 'loop' && loopEnabled ? 'active' : ''} ${pad.action === 'kill' && isMuted ? 'active danger-pad' : ''}`}
                        key={pad.label}
                        onClick={() => togglePad(pad)}
                        type="button"
                    >
                        <span>{pad.label}</span>
                    </button>
                ))}
            </div>

            <div className="board-rack">
                <div className="rack-panel tempo-panel">
                    <h3>Tempo Core</h3>
                    <div className="tempo-readout">
                        <strong>{tempo}</strong>
                        <span>BPM</span>
                    </div>
                    <label className="tempo-control">
                        <span>Playback Rate</span>
                        <input
                            aria-label="Tempo"
                            className="dj-slider"
                            type="range"
                            min="88"
                            max="154"
                            value={tempo}
                            onChange={(event) => setTempo(Number(event.target.value))}
                        />
                    </label>
                    <label className="tempo-control">
                        <span>Master</span>
                        <input
                            aria-label="Master volume"
                            className="dj-slider"
                            type="range"
                            min="0"
                            max="100"
                            value={masterLevel}
                            onChange={(event) => setMasterLevel(Number(event.target.value))}
                        />
                    </label>
                </div>

                <div className="rack-panel">
                    <h3>EQ Discipline</h3>
                    <div className="mini-controls">
                        {Object.entries(eq).map(([control, value]) => (
                            <label key={control}>
                                <span>{control}</span>
                                <input
                                    className="dj-slider"
                                    type="range"
                                    min="0"
                                    max="100"
                                    value={value}
                                    onChange={(event) => updateEq(control, Number(event.target.value))}
                                />
                            </label>
                        ))}
                    </div>
                </div>

                <div className="rack-panel fx-rack">
                    <h3>FX Bank</h3>
                    <div className="fx-buttons">
                        {effects.map((effect) => (
                            <button
                                aria-pressed={activeEffects.includes(effect)}
                                className={`fx-button ${activeEffects.includes(effect) ? 'active' : ''}`}
                                key={effect}
                                onClick={() => toggleEffect(effect)}
                                type="button"
                            >
                                {effect}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <audio ref={audioRef} src={music} preload="metadata" />
        </div>
    );
}

export default DjPanel;
